/**
 * @name CancelDialog
 * @description Modal para cancelar un backup
 * @author Ricardo Guerra
 */

import React from 'react';
import i18n from 'i18next';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Formik } from 'formik';
import moment from 'moment';
import styled from 'styled-components';

import { Icons, Layout, MUI, Typography } from '@styles';

const CancelDialog = (props) => {
  const {
    data,
    open,
    onClose,
    onCancelBackup
  } = props;

  const values = {
    comment: '',
  };

  const schema = Yup.object({
    comment: Yup.string()
      .min(2, i18n.t('FormsGeneralTexts.min2Chars'))
      .max(250, i18n.t('FormsGeneralTexts.max250Chars'))
      .required('*')
  });

  return (
    <MUI.Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
    >
      <MUI.DialogTitle>
        {i18n.t('BackupSettings.cancelBackup')}
        <MUI.DialogBoxClose onClick={onClose}>
          <Icons.Close />
        </MUI.DialogBoxClose>
      </MUI.DialogTitle>
      <Formik
        initialValues={values}
        validationSchema={schema}
        isInitialValid={schema.isValidSync(values)}
        onSubmit={(values, { setSubmitting }) => {
          onCancelBackup(values, data, setSubmitting);
        }}
      >
        {(formikProps) => (
          <>
            <MUI.DialogContent>
              <MUI.Box padding="8px">
                <Typography.Text>
                  {i18n.t('BackupSettings.sureCancelBackup')}
                </Typography.Text>
              </MUI.Box>
              <MUI.Box padding="8px">
                {/* Backup */}
                <RowBox>
                  <Icons.Person />
                  <Typography.Text>
                    <Typography.Strong>{`${i18n.t('Backup')}: `}</Typography.Strong>
                    {data.NombreBackup}
                  </Typography.Text>
                </RowBox>
                {/* Fecha Inicio Backup */}
                <RowBox>
                  <Icons.Date />
                  <Typography.Text>
                    <Typography.Strong>{`${i18n.t('GenericMessages.startDate')}: `}</Typography.Strong>
                    {moment(data.FechaInicio).format(i18n.t('FormatDates.short'))}
                  </Typography.Text>
                </RowBox>
                {/* Fecha Fin Backup */}
                <RowBox>
                  <Icons.Date />
                  <Typography.Text>
                    <Typography.Strong>{`${i18n.t('GenericMessages.endDate')}: `}</Typography.Strong>
                    {moment(data.FechaFin).format(i18n.t('FormatDates.short'))}
                  </Typography.Text>
                </RowBox>
                {/* Comentario Backup */}
                <RowBox>
                  <Typography.Text>
                    <Typography.Strong>{`${i18n.t('GenericMessages.comment')}: `}</Typography.Strong>
                    {data.Comentario}
                  </Typography.Text>
                </RowBox>
              </MUI.Box>
              <MUI.Box padding="8px">
                <Typography.Text>
                  {i18n.t('BackupSettings.commentCancelBackup')}
                </Typography.Text>
              </MUI.Box>
              {/* Comentario */}
              <MUI.Box padding="8px">
                <MUI.TextField
                  multiline
                  label={i18n.t('GenericMessages.comment')}
                  type="text"
                  name="comment"
                  //placeholder="KKKK"
                  value={formikProps.values.comment}
                  characterLimit={250}
                  onBlur={formikProps.handleBlur}
                  onChange={formikProps.handleChange}
                  error={Boolean(formikProps.touched.comment && formikProps.errors.comment)}
                  helperText={formikProps.touched.comment && formikProps.errors.comment}
                  inputProps={{ maxLength: '250' }}
                />
              </MUI.Box>
            </MUI.DialogContent>

            <MUI.DialogActions>
              <Layout.BoxRequired>
                {`* ${i18n.t('FormsGeneralTexts.required')}`}
              </Layout.BoxRequired>
              <MUI.ButtonText onClick={onClose}>
                {i18n.t('DialogConfirm.cancel')}
              </MUI.ButtonText>
              <MUI.ButtonCTA
                type="button"
                color="primary"
                disabled={!formikProps.isValid || formikProps.isSubmitting}
                onClick={formikProps.handleSubmit}
              >
                {i18n.t('DialogConfirm.confirm')}
              </MUI.ButtonCTA>
            </MUI.DialogActions>
          </>
        )}
      </Formik>
    </MUI.Dialog>
  );
};

CancelDialog.propTypes = {
  // Objeto con información para el modal.
  data: PropTypes.object,
  // Boolean que indica si el modal esta abierto.
  open: PropTypes.bool.isRequired,
  // Función que se ejecuta cuando se cierra el modal.
  onClose: PropTypes.func.isRequired,
  // Función que se ejecuta para cancelar el backup.
  onCancelBackup: PropTypes.func.isRequired,
};

// *************************************
// STYLED COMPONENTS                               
// *************************************

const RowBox = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 8px;

  .MuiSvgIcon-root {
    margin-right: 8px;
    font-size: 20px;
  }
`;

export default CancelDialog;
